import { useState } from 'react';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Alert from '@mui/material/Alert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { saveInfoContact } from 'app/services/api/admin/charges';
import Loader from 'app/common/Loader';
import LoadSuccess from '../components/load-success/LoadSuccess';
import CtaButton from '../components/CtaButton/CtaButton';
import backgroundSection3 from "../../../images/backgroundSection3.jpg";

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  email: Yup.string().email('Email inválido').required('El email es obligatorio'),
  company: Yup.string().required('La empresa es obligatoria'),
  role: Yup.string().required('El rol es obligatorio'),
  need: Yup.string().required('Describe tu necesidad'),
});

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '1.6rem',
    '&:hover fieldset': { borderColor: '#1A56DB' },
    '&.Mui-focused fieldset': { borderColor: '#1A56DB' },
  },
  '& .MuiInputLabel-root': { fontSize: '1.6rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1A56DB' },
};

const TRUST_ITEMS = [
  { Icon: SupportAgentIcon, text: "Respuesta en menos de 24 horas (hábiles)" },
  { Icon: VerifiedIcon, text: "Evaluación sin compromiso" },
  { Icon: SecurityIcon, text: "Tu información está protegida" },
];

function Contact() {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: { name: '', email: '', company: '', role: '', need: '', requestType: '' },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      setError('');
      try {
        const { be, ...res } = await saveInfoContact(values);
        if (be !== 'ok' || res?.data?.status !== true) {
          setError(res?.data?.codeMessages?.[0]?.message || 'Error al enviar. Intenta de nuevo.');
          setLoading(false);
          return;
        }
        setLoading(false);
        setShowSuccess(true);
        resetForm();
        setTimeout(() => setShowSuccess(false), 3000);
      } catch {
        setError('Error al enviar. Intenta de nuevo.');
        setLoading(false);
      }
    },
  });

  const handleSubmit = (requestType) => {
    formik.setFieldValue('requestType', requestType);
    setTimeout(() => formik.handleSubmit(), 0);
  };

  return (
    <>
      <Loader visible={loading} />
      <Dialog open={showSuccess}>
        <DialogContent>
          <LoadSuccess message="" />
        </DialogContent>
      </Dialog>

      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col md:flex-row flex-1 min-h-[calc(100vh-64px)]">

          {/* Left panel */}
          <div
            className="shrink-0 md:basis-[42%] bg-no-repeat bg-cover bg-center py-56 px-32 md:py-72 md:px-56 flex flex-col justify-center gap-32"
            style={{ backgroundImage: `linear-gradient(rgba(14,30,80,0.92),rgba(14,30,80,0.92)), url(${backgroundSection3})` }}
          >
            <p className="text-14 font-bold uppercase tracking-widest text-[#38BDF8]">
              Contacto
            </p>
            <h1 className="text-36 md:text-48 font-extrabold text-white leading-[1.2]">
              Hablemos de tu operación
            </h1>
            <p className="text-18 text-white/80 leading-[1.75] max-w-[380px]">
              Nuestro equipo te ayudará a evaluar cómo EcoRep puede integrarse a tu operación y mejorar tu gestión.
            </p>
            <div className="flex flex-col gap-16 mt-8">
              {TRUST_ITEMS.map(({ Icon, text }) => (
                <div key={text} className="flex items-center gap-14 text-16 text-white/80">
                  <Icon className="!text-20 text-[#38BDF8] shrink-0" />
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 bg-[#F8FAFC] py-56 px-24 md:py-72 md:px-56 flex items-center justify-center">
            <div className="bg-white rounded-20 py-36 px-28 md:py-52 md:px-44 w-full max-w-[520px] shadow-[0_4px_24px_rgba(26,86,219,0.08)]">
              <h2 className="text-24 font-bold text-[#1E3A8A] mb-32">
                Cuéntanos sobre ti
              </h2>

              <div className="flex flex-col gap-20 mb-32">
                <TextField
                  label="Nombre"
                  name="name"
                  variant="outlined"
                  fullWidth
                  sx={FIELD_SX}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  sx={FIELD_SX}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Empresa"
                      name="company"
                      variant="outlined"
                      fullWidth
                      sx={FIELD_SX}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.company}
                      error={formik.touched.company && Boolean(formik.errors.company)}
                      helperText={formik.touched.company && formik.errors.company}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Rol"
                      name="role"
                      variant="outlined"
                      fullWidth
                      sx={FIELD_SX}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.role}
                      error={formik.touched.role && Boolean(formik.errors.role)}
                      helperText={formik.touched.role && formik.errors.role}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Necesidad"
                  name="need"
                  variant="outlined"
                  fullWidth
                  sx={FIELD_SX}
                  multiline
                  rows={3}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.need}
                  error={formik.touched.need && Boolean(formik.errors.need)}
                  helperText={formik.touched.need && formik.errors.need}
                  placeholder="Cuéntanos brevemente qué necesitas resolver..."
                />
              </div>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}

              <div className="flex flex-col gap-12">
                <CtaButton
                  fullWidth
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => handleSubmit('demo')}
                  disabled={loading}
                  sx={{ padding: '15px 24px', borderRadius: '10px', justifyContent: 'flex-start', gap: '10px' }}
                >
                  Solicitar demo
                </CtaButton>
                <CtaButton
                  variant="secondary-light"
                  fullWidth
                  startIcon={<HeadsetMicIcon />}
                  onClick={() => handleSubmit('asesoria')}
                  disabled={loading}
                  sx={{ padding: '15px 24px', borderRadius: '10px', justifyContent: 'flex-start', gap: '10px' }}
                >
                  Recibir asesoría
                </CtaButton>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default Contact;
